import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { GridColumns } from '@mui/x-data-grid-pro';
import { GridToolbarDensitySelector } from 'components/ButtonComponent/ButtonDensity';
import { GridToolbarFilterButton } from 'components/ButtonComponent/ButtonFilter';
import { CustomDataTable } from 'components/DatatableComponent';
import { MainLayoutComponent } from 'components/LayoutComponent/SidebarLayout/MainLayoutComponent';
import { DASHBOARD_VIEW_MODE } from 'constant/DashboardTypeConstant';
import { RootState } from 'store';
import {
  useDeleteMemberMappingDefinition,
  useMemberMappingDefinitions,
} from 'services/v1/SystemTenant/AROCube/MemberMappingDefinitionService';
import { MemberMappingDefinition } from 'types/api/SystemTenant/AROCube/MemberMappingDefinitionTypes';
import { MemberMappingEditor } from './MemberMappingEditor';

function MemberMappingToolbar({
  readOnly,
  search,
  loading,
  onSearch,
  onCreate,
  onRefresh,
}: {
  readOnly: boolean;
  search: string;
  loading: boolean;
  onSearch: (value: string) => void;
  onCreate: () => void;
  onRefresh: () => void;
}) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} justifyContent='space-between' sx={{ py: 1.8 }}>
      {!readOnly && (
        <Button
          variant='main-table-panel'
          startIcon={<AddCircleOutlineOutlinedIcon />}
          sx={{ alignSelf: 'flex-start', letterSpacing: 0, flexShrink: 0 }}
          onClick={onCreate}
        >
          Create Member Mapping
        </Button>
      )}
      <Stack direction='row' spacing={0.5} alignItems='center' sx={{ ml: { sm: 'auto' }, minWidth: 0 }}>
        <TextField
          placeholder='Search mappings...'
          size='small'
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          inputProps={{ 'aria-label': 'Search mappings' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchRoundedIcon sx={{ color: '#98A2AE', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 260 }, minWidth: 0 }}
        />
        <GridToolbarFilterButton />
        <Tooltip title='Row density'>
          <GridToolbarDensitySelector />
        </Tooltip>
        <Tooltip title='Refresh mappings'>
          <span>
            <IconButton size='small' aria-label='Refresh mappings' disabled={loading} onClick={onRefresh}>
              <RefreshRoundedIcon sx={{ color: loading ? 'action.disabled' : '#42BB93' }} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

export default function MemberMappingDefinitionPage() {
  const { t } = useTranslation();
  const readOnly = useSelector((state: RootState) => state.auth.dashboardViewMode) === DASHBOARD_VIEW_MODE.VIEW_ONLY;
  const definitions = useMemberMappingDefinitions();
  const deletion = useDeleteMemberMappingDefinition();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [editor, setEditor] = useState<{ open: boolean; definition?: MemberMappingDefinition }>({ open: false });
  const [deleteTarget, setDeleteTarget] = useState<MemberMappingDefinition | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const rows = useMemo(
    () =>
      (definitions.data?.data ?? []).filter((row) =>
        `${row.name} ${row.sourceMember.label} ${row.destinationMember.label}`
          .toLowerCase()
          .includes(search.toLowerCase())
      ),
    [definitions.data, search]
  );
  const { reset: resetDeletion } = deletion;
  const columns = useMemo<GridColumns<MemberMappingDefinition>>(
    () => [
      { field: 'name', headerName: 'Mapping Name', minWidth: 220, flex: 1.3 },
      {
        field: 'sourceMember',
        headerName: 'Source Member Tree',
        minWidth: 200,
        flex: 1,
        valueGetter: ({ row }) => row.sourceMember.label,
      },
      {
        field: 'destinationMember',
        headerName: 'Destination Member Tree',
        minWidth: 200,
        flex: 1,
        valueGetter: ({ row }) => row.destinationMember.label,
      },
      {
        field: 'fieldMatches',
        headerName: 'Conditions',
        width: 110,
        type: 'number',
        valueGetter: ({ row }) => row.fieldMatches.length,
      },
      {
        field: 'referencesCount',
        headerName: 'Used by Attributes',
        width: 150,
        type: 'number',
        valueGetter: ({ row }) => row.referencesCount ?? 0,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: readOnly ? 70 : 90,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row, tabIndex }) => (
          <Stack direction='row' spacing={0.5}>
            <Tooltip title={readOnly ? 'View mapping' : 'Edit mapping'}>
              <IconButton
                size='small'
                tabIndex={tabIndex}
                aria-label={`${readOnly ? 'View' : 'Edit'} ${row.name}`}
                onClick={() => setEditor({ open: true, definition: row })}
              >
                {readOnly ? <VisibilityOutlinedIcon fontSize='small' /> : <EditOutlinedIcon fontSize='small' />}
              </IconButton>
            </Tooltip>
            {!readOnly && (
              <Tooltip title={(row.referencesCount ?? 0) > 0 ? 'Used by cube attributes' : 'Delete mapping'}>
                <span>
                  <IconButton
                    size='small'
                    tabIndex={tabIndex}
                    aria-label={`Delete ${row.name}`}
                    disabled={(row.referencesCount ?? 0) > 0}
                    onClick={() => {
                      resetDeletion();
                      setDeleteTarget(row);
                    }}
                  >
                    <DeleteOutlineRoundedIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [readOnly, resetDeletion]
  );
  const closeDelete = () => {
    if (!deletion.isLoading) setDeleteTarget(null);
  };

  return (
    <MainLayoutComponent pageTitle='Member Mapping Definition' breadcrumbs={[t('SIDEBAR_MENU.DASHBOARD'), 'ARO Cube']}>
      {definitions.isError && (
        <Alert severity='error' sx={{ m: 1.25 }} action={<Button onClick={() => definitions.refetch()}>Retry</Button>}>
          Unable to load member mapping definitions.
        </Alert>
      )}
      <CustomDataTable
        aria-label='Member mapping definitions'
        rows={rows}
        columns={columns}
        loading={definitions.isFetching}
        checkboxSelection={false}
        disableSelectionOnClick
        paginationMode='client'
        sortingMode='client'
        filterMode='client'
        page={page}
        pageSize={pageSize}
        rowsPerPageOptions={[5, 10, 20]}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(0);
        }}
        onFilterModelChange={() => setPage(0)}
        initialState={{ pinnedColumns: { right: ['actions'] } }}
        getRowClassName={({ indexRelativeToCurrentPage }) => (indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
        components={{ Toolbar: MemberMappingToolbar }}
        componentsProps={{
          panel: {
            sx: {
              '@media (max-width: 599.95px)': {
                width: 'calc(100vw - 32px)',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiDataGrid-paper': { minWidth: 0, width: '100%' },
                '& .MuiDataGrid-panelWrapper': { minWidth: 0 },
                '& .MuiDataGrid-filterForm': { flexWrap: 'wrap', gap: 1 },
                '& .MuiDataGrid-filterFormColumnInput, & .MuiDataGrid-filterFormOperatorInput, & .MuiDataGrid-filterFormValueInput':
                  {
                    width: '100%',
                    minWidth: 0,
                  },
              },
            },
          },
          toolbar: {
            readOnly,
            search,
            loading: definitions.isFetching,
            onSearch: (value: string) => {
              setSearch(value);
              setPage(0);
            },
            onCreate: () => {
              setMessage(null);
              setEditor({ open: true });
            },
            onRefresh: () => definitions.refetch(),
          },
          noRowsOverlay: {
            label: definitions.isError
              ? 'Member mapping definitions are unavailable.'
              : search
              ? 'No mappings match your search.'
              : 'No member mapping definitions yet.',
          },
        }}
        sx={{
          bgcolor: '#fff',
          '& .MuiDataGrid-pinnedColumnHeaders--right': { bgcolor: '#EDF2FF', boxShadow: 'none' },
          '& .MuiDataGrid-pinnedColumns--right': { boxShadow: '-1px 0 0 #E3EBF6' },
          '& .MuiDataGrid-footerContainer': { flexWrap: 'wrap' },
          '& .MuiTablePagination-toolbar': { px: 0.5 },
        }}
      />
      {editor.open && (
        <MemberMappingEditor
          open
          initial={editor.definition}
          readOnly={readOnly}
          onClose={() => setEditor({ open: false })}
          onSaved={(row) => {
            setEditor({ open: false });
            setMessage(`Saved ${row.name}.`);
          }}
        />
      )}
      <Dialog
        open={!!deleteTarget}
        onClose={closeDelete}
        fullWidth
        maxWidth='xs'
        PaperProps={{ sx: { m: 2, width: 'calc(100% - 32px)' } }}
        aria-labelledby='delete-member-mapping-title'
      >
        <DialogTitle component='div' sx={{ px: 3, py: 1.5 }}>
          <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
            <Typography id='delete-member-mapping-title' component='h2' variant='modal-title'>
              Delete Member Mapping
            </Typography>
            <Tooltip title='Close'>
              <span>
                <IconButton aria-label='Close delete confirmation' onClick={closeDelete} disabled={deletion.isLoading}>
                  <HighlightOffRoundedIcon sx={{ color: '#98A2AE' }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: '#E3EBF6' }}>
          <Typography variant='body2'>Delete this member mapping definition?</Typography>
          <Typography variant='body2' fontWeight='bold' sx={{ mt: 1, overflowWrap: 'anywhere' }}>
            {deleteTarget?.name}
          </Typography>
          {deletion.isError && (
            <Alert severity='error' sx={{ mt: 2 }}>
              {deletion.error?.response?.data?.message || 'Unable to delete this mapping.'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant='main-table-panel-border'
            sx={{ letterSpacing: 0 }}
            disabled={deletion.isLoading}
            onClick={closeDelete}
          >
            Cancel
          </Button>
          <LoadingButton
            variant='action-warning'
            startIcon={<DeleteOutlineRoundedIcon />}
            sx={{ letterSpacing: 0 }}
            loading={deletion.isLoading}
            onClick={() =>
              deleteTarget &&
              deletion.mutate(deleteTarget.id, {
                onSuccess: () => {
                  setMessage(`Deleted ${deleteTarget.name}.`);
                  setDeleteTarget(null);
                },
              })
            }
          >
            Delete Mapping
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!message}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setMessage(null)}
      >
        <Alert severity='success' onClose={() => setMessage(null)} sx={{ overflowWrap: 'anywhere' }}>
          {message}
        </Alert>
      </Snackbar>
    </MainLayoutComponent>
  );
}
